import { COLORS } from '@btime/constants'
import { capitalizeIfLower, capitalizeSentenceIfLower } from '@btime/helpers'
import { compose, find, is, path, pathOr, prop, propEq, propOr } from 'ramda'
import React, { Fragment } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { usePdfOptionsForm } from './options/_form/PdfOptionsFormHandler'
import Gallery from './Gallery'
import Grid, { Col } from './Grid'
import Image from './Image'
import LabelValue from './LabelValue'
import Line from './Line'
import Markdown from '../presentation/Markdown'
import Separator from './Separator'
import Tag from './Tag'
import Text from './Text'
import View from './View'
import Stock from './stock/Stock'
import MapStaticImage from '../map/MapStaticImage'

export const getValueFromOptions = (value, options) =>
  compose(capitalizeIfLower, propOr('__resposta_não_encontrada__', 'name'), find(propEq('id', value)))(options)

// TODO: Break component and reorganize folder
const getValue = (item, customReplyPresenter, tComponents, tCommon) => {
  const type = compose(path(['question', 'type', 'name']))(item)
  const questionData = compose(path(['question', 'data']))(item) || {}
  const value = prop(['value'], item)

  if (value === undefined || !type) return { value }

  if (value === null && type !== 'action_plan') {
    return {
      value: <Tag label={tComponents('notApplicable')} color={COLORS.dark3} />,
      plainValue: true
    }
  }

  switch (type) {
    case 'compliance':
    case 'checkbox':
      var active = value == 1
      const { labels } = questionData
      return {
        value: (
          <Tag
            label={active ? labels?.true || tCommon('yes') : labels?.false || tCommon('no')}
            color={active ? COLORS.green : COLORS.red}
          />
        ),
        plainValue: true
      }

    case 'multiple':
      return { value: value.map(item => item.name).join(', ') }

    case 'pending':
      const pendencyName = compose(capitalizeIfLower, path(['pending', 'name']))(value)
      const pendencyDescription = compose(capitalizeSentenceIfLower, prop('description'))(value)

      return {
        value: <Tag label={pendencyName} color={COLORS.purple} />,
        plainValue: true,
        children: <Text>{pendencyDescription}</Text>
      }

    case 'datetime':
    case 'date':
      return {
        value: moment(value).format('DD/MM/YYYY' + (type === 'datetime' ? ' HH:mm' : ''))
      }

    case 'select':
      return { value: value?.name }

    case 'system_status':
      var active = value == 1
      return {
        value: <Tag label={active ? tComponents('pdf.active') : tComponents('pdf.inactive')} color={active ? COLORS.green : COLORS.red} />,
        plainValue: true
      }

    case 'sign':
      const src = prop('src', value)
      const name = value.name
      const doc = value.document ? `(${value.document})` : ''
      return {
        value: `${name} - ${doc}`,
        children: !!src && <Image src={src} style={{ height: 'auto', width: '55%' }} type="sign" />
      }

    case 'gallery':
      const items = (value || []).map(item => ({ ...propOr({}, 'document', item), ...item }))
      const numberOfItems = items.length
      const itemNumberLabel = tComponents('pdf.itemCount', { count: numberOfItems })

      return {
        value: itemNumberLabel,
        children: (
          <Fragment>
            <Separator weight={4} />
            <Gallery items={items} />
          </Fragment>
        )
      }

    case 'block':
      const blocks = Array.isArray(value) ? value : []
      return {
        value: tComponents('pdf.itemCount', { count: blocks.length }),
        children: (
          <>
            <Separator weight={4} />
            {blocks.map((replies, index) => (
              <View key={index} align="stretch">
                <Grid align="stretch">
                  <Line vertical width={5} height="auto" />
                  <Col width={12} />
                  <Col flex={1}>
                    {replies.map(reply => (
                      <Reply key={reply.id} reply={reply} />
                    ))}
                  </Col>
                </Grid>
                <Separator weight={4} />
              </View>
            ))}
            {customReplyPresenter ? customReplyPresenter(item, blocks) : <></>}
          </>
        )
      }

    case 'location':
      return {
        value: path(['search'], value)
      }

    case 'place_location':
    case 'place_person':
      const places = prop('places', item) || value || []
      return {
        value: places.map(place => `${place.name} (${place.resume})`).join(', ')
      }

    case 'place':
    case 'user':
    case 'asset':
      if (is(Array, value)) {
        return { value: value.map(prop('name')).join(', ') }
      }
      return { value: value.name }

    case 'action_plan':
      return {
        value: value?.map(item => item.yearlyId).join(', ')
      }

    case 'stock':
      return {
        value: value.item.name,
        children: <Stock value={value} />
      }

    case 'coordinates':
      const { latitude, longitude } = value
      return {
        value: <MapStaticImage latitude={latitude} longitude={longitude} zoom={15} />
      }

    default:
      return { value: (value || '').toString() }
  }
}

const Reply = ({ reply, customReplyPresenter }) => {
  const { hideImages, showQuestionInfo, hidePrivateQuestion } = usePdfOptionsForm()
  const { t: tComponents } = useTranslation('components')
  const { t: tCommon } = useTranslation('common')

  const isPrivateReply = reply.question.private
  const shouldHidePrivateReply = isPrivateReply && hidePrivateQuestion

  const label = compose(capitalizeSentenceIfLower, path(['question', 'name']))(reply)
  const notes = prop('notes', reply)

  const images = hideImages ? false : prop('documents', reply)

  const questionDocuments = pathOr([], ['question', 'documents'], reply)
  const questionDescription = path(['question', 'description'], reply)

  const { children, ...props } = getValue(reply, customReplyPresenter, tComponents, tCommon)

  if (shouldHidePrivateReply) return false

  return (
    <View noWrap>
      <LabelValue label={label} {...props}>
        {!!showQuestionInfo && (
          <>
            {!!questionDescription && <Markdown>{questionDescription}</Markdown>}
            {!!questionDocuments.length && <Gallery items={questionDocuments} />}
          </>
        )}

        {children}
        {!!notes && (
          <div>
            <Separator weight={4} />
            <Text size="xxsmall" color={COLORS.dark4}>
              {tComponents('pdf.notes')}
            </Text>
            <Separator weight={1} />
            <Text>{notes}</Text>
          </div>
        )}
        {!!images && !!images.length && (
          <div>
            <Separator weight={4} />
            <Text size="xxsmall" bold color={COLORS.dark4}>
              {tComponents('pdf.photos')}
            </Text>
            <Separator weight={1} />
            <Gallery items={images} />
          </div>
        )}
      </LabelValue>
    </View>
  )
}

export default Reply
